import React, { useState, useEffect, useRef } from 'react';
import { MantineProvider, Collapse, Box, Text, UnstyledButton, Loader } from '@mantine/core';
import {
  IconPlus,
  IconList,
  IconEdit,
  IconMicrophone,
  IconVideo,
  IconSettings,
  IconChevronUp,
  IconChevronDown,
  IconSearch,
  IconMapPin,
  IconWorld,
  IconX,
  IconLogout,
  IconLogin,
  IconUserPlus,
  IconClipboardText,
  IconSchool,
  IconCoin,
  IconShoppingCartSearch,
  IconStars,
  IconCalendarWeek,
  IconCalendarPlus,
  IconBookmarks,
  IconBriefcase2,
  IconBrowserPlus,
  IconAlertTriangle,
  IconUsersGroup,
  IconCircles,
  IconArticle,
  IconCalendarClock,
  IconCashBanknote,
  IconTrash,
  IconPalette,
  IconAd,
} from '@tabler/icons-react';
import { Link } from '@inertiajs/react';
import { router } from '@inertiajs/react';
import '../styles/Sidebar.css';
import { useUser } from '../src/context/UserContext';

interface SidebarProps {
  user?: {
    id: number;
    email: string;
    username: string;
    roles: string[];
    avatar_url: string | null;
  } | null;
  isOpen: boolean;
  closeSidebar: () => void;
}

const DEFAULT_PAGE = 1;
const DEFAULT_ITEMS = 20;
const BASE = `/posts?page=${DEFAULT_PAGE}&items=${DEFAULT_ITEMS}`;

// Quick Links at the top
const quickLinks = [
  {
    label: 'Cities',
    icon: IconMapPin,
    link: '/cities',
    description: 'Explore Local Cities',
  },
  {
    label: 'Global Calendars',
    icon: IconCalendarWeek,
    link: '/dance_styles',
    description: 'Dance Style Calendars',
  },
];

const menuData = [
  {
    title: 'Posts',
    icon: IconList,
    items: [
      { label: 'All Posts', icon: IconList, link: '/posts' },
      { label: 'Blogs', icon: IconEdit, link: `${BASE}&post_types=1` },
      { label: 'Podcasts', icon: IconMicrophone, link: `${BASE}&post_types=2` },
      { label: 'Videos', icon: IconVideo, link: `${BASE}&post_types=3` },
      {
        label: 'Create New Post',
        icon: IconPlus,
        roles: ['SuperAdmin', 'Blogger'],
        link: '/posts/new',
        requiresLogin: true,
      },
      {
        label: 'Manage Your Posts',
        icon: IconSettings,
        roles: ['SuperAdmin', 'Blogger'],
        link: '/posts/user_posts',
        requiresLogin: true,
      },
    ],
  },
  {
    title: 'Events',
    icon: IconWorld,
    items: [
      { label: 'Add a new event', icon: IconCalendarPlus, link: '/suggest_event', requiresLogin: true },
      { label: 'Explore Events', icon: IconSearch, link: '/events' },
      { label: 'Explore Local Cities', icon: IconMapPin, link: '/cities' },
      { label: 'Dance Style Calendars', icon: IconCalendarWeek, link: '/dance_styles' },
      {
        label: 'Your Bookmarked Events',
        icon: IconBookmarks,
        link: '/bookmarked-events',
        requiresLogin: true,
      },
      {
        label: 'Events You Manage',
        icon: IconBriefcase2,
        link: '/users/:username/managed-events',
        requiresLogin: true,
      },
      {
        label: 'Create new event type',
        icon: IconBrowserPlus,
        roles: ['SuperAdmin'],
        link: '/event_types/new',
        requiresLogin: true,
      },
      { label: 'View event types', icon: IconSearch, roles: ['SuperAdmin'], link: '/event_types', requiresLogin: true },
      {
        label: 'Create new event frequency',
        icon: IconBrowserPlus,
        roles: ['SuperAdmin'],
        link: '/event_frequencies/new',
        requiresLogin: true,
      },
      {
        label: 'View event frequencies',
        icon: IconSearch,
        roles: ['SuperAdmin'],
        link: '/event_frequencies',
        requiresLogin: true,
      },
    ],
  },
  {
    title: 'Artists',
    icon: IconPalette,
    items: [
      { label: 'Add New Artist', icon: IconUserPlus, link: '/suggest_artist', requiresLogin: true },
      { label: 'Explore Artists', icon: IconSearch, link: '/artists' },
      {
        label: 'Manage Your Artists',
        icon: IconSettings,
        roles: ['Artist'],
        link: '/users/:username/managed-artists',
        requiresLogin: true,
      },
      {
        label: 'Artist Booking Portal',
        icon: IconClipboardText,
        roles: ['SuperAdmin', 'Artist'],
        link: '/users/:username/your-booking-portals',
        requiresLogin: true,
      },
      {
        label: 'Private Lessons',
        icon: IconSchool,
        roles: ['Artist'],
        link: 'users/:username/create_private_lessons?active_tab=pl-enable',
        requiresLogin: true,
      },
      {
        label: 'Payouts',
        icon: IconCoin,
        roles: ['Artist', 'Editor'],
        link: '/users/:username/user-payments',
        requiresLogin: true,
      },
      {
        label: 'Your Orders',
        icon: IconShoppingCartSearch,
        roles: ['Artist', 'Editor'],
        link: '/users/:username/artist-orders',
        requiresLogin: true,
      },
      {
        label: 'My Supporters',
        icon: IconStars,
        roles: ['Artist', 'Editor'],
        link: '/users/:username/user-supporters',
        requiresLogin: true,
      },
    ],
  },
  {
    title: 'Manage',
    icon: IconSettings,
    items: [
      {
        label: 'Your Private Lessons',
        icon: IconSchool,
        link: '/users/:username/your_private_lessons',
        requiresLogin: true,
      },
      {
        label: 'Pending',
        icon: IconAlertTriangle,
        roles: ['SuperAdmin', 'Admin'],
        link: '/pending-records',
        requiresLogin: true,
      },
      {
        label: 'Manage Users',
        icon: IconUsersGroup,
        roles: ['SuperAdmin', 'Admin', 'Moderator'],
        link: '/users',
        requiresLogin: true,
      },
      { label: 'Manage Roles', icon: IconCircles, roles: ['SuperAdmin'], link: '/roles', requiresLogin: true },
      {
        label: 'Manage Organizers',
        icon: IconBriefcase2,
        roles: ['SuperAdmin'],
        link: '/o',
        requiresLogin: true,
      },
      {
        label: 'Manage Your Organizations',
        icon: IconBriefcase2,
        roles: ['Organizer'],
        link: '/users/:username/manage-organizations',
        requiresLogin: true,
      },
      {
        label: 'Manage Subscriptions',
        icon: IconArticle,
        roles: ['SuperAdmin'],
        link: '/subscriptions/manage',
        requiresLogin: true,
      },
      {
        label: 'Manage Videos',
        icon: IconVideo,
        roles: ['SuperAdmin', 'Editor'],
        link: '/videos',
        requiresLogin: true,
      },
      {
        label: 'Manage Recurring Events',
        icon: IconCalendarClock,
        roles: ['SuperAdmin', 'Editor'],
        link: '/re',
        requiresLogin: true,
      },
      {
        label: 'Manage Star Purchases',
        icon: IconStars,
        roles: ['SuperAdmin'],
        link: '/admin/star-purchases',
        requiresLogin: true,
      },
      {
        label: 'Manage Ads',
        icon: IconAd,
        roles: ['SuperAdmin'],
        link: '/admin/ads',
        requiresLogin: true,
      },
      {
        label: 'Claimed Artist Dashboard',
        icon: IconClipboardText,
        roles: ['SuperAdmin'],
        link: '/admin/user_artist_dashboard',
        requiresLogin: true,
      },
      {
        label: 'Manage Cities',
        icon: IconMapPin,
        roles: ['SuperAdmin'],
        link: '/admin/city-index',
        requiresLogin: true,
      },
      {
        label: 'Search History Dashboard',
        icon: IconSearch,
        roles: ['SuperAdmin', 'Editor'],
        link: '/admin/search_histories',
        requiresLogin: true,
      },
      {
        label: 'Deleted Items',
        icon: IconTrash,
        roles: ['SuperAdmin', 'Admin'],
        link: '/discarded-items',
        requiresLogin: true,
      },
      { label: 'Manage Settings', icon: IconSettings, link: '/users/:username/user-settings', requiresLogin: true },
    ],
  },
];

const Sidebar: React.FC<SidebarProps> = ({ isOpen, closeSidebar, user: propUser }) => {
  const { user: contextUser, logout } = useUser();
  const user = propUser || contextUser;
  const userRoles = user?.roles || [];

  // State to keep track of which menu item is loading
  const [loadingItem, setLoadingItem] = useState<string | null>(null);

  const sidebarRef = useRef<HTMLDivElement | null>(null);

  const handleLogout = async (e: React.FormEvent) => {
    e.preventDefault();
    await logout();
  };

  const getLoginUrl = () => {
    const currentPath = window.location.pathname;
    return `/login?return_to=${encodeURIComponent(currentPath)}`;
  };

  const getDynamicLink = (link: string) => {
    if (!user) return link;
    return link.replace(':username', user.username);
  };

  // Toggles the open state for collapsible sections
  const toggleSection = (title: string) => {
    setOpenSections((prev) => ({
      ...prev,
      [title]: !prev[title],
    }));
  };

  // Listen for clicks outside the sidebar to close it
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (sidebarRef.current && !sidebarRef.current.contains(event.target as Node)) {
        closeSidebar();
      }
    }
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, closeSidebar]);

  // Listen for Inertia navigation events to clear the loading state
  useEffect(() => {
    const clearLoading = () => setLoadingItem(null);
    const finishHandler = () => setLoadingItem(null);
    const errorHandler = () => setLoadingItem(null);

    router.on('finish', finishHandler);
    router.on('error', errorHandler);

    return () => {
      router.on('finish', () => {}); // Cleanup by reassigning to a no-op function
      router.on('error', () => {}); // Cleanup by reassigning to a no-op function
    };
  }, []);

  // Keep track of open sections for collapsible items
  const [openSections, setOpenSections] = useState<{ [key: string]: boolean }>({});

  return (
    <MantineProvider>
      <div ref={sidebarRef} className={`sidebar ${isOpen ? 'open' : ''}`}>
        <Box className="sidebar-header">
          <UnstyledButton onClick={closeSidebar} className="close-button">
            <IconX size={34} />
          </UnstyledButton>
        </Box>

        <Box className="title-section">
          <Text size="xl" fw={500} className="title">
            Where Can We Dance
          </Text>
        </Box>

        {/* Quick Links Section */}
        <Box
          className="quick-links-section"
          style={{ marginBottom: '20px', marginTop: '16px', borderBottom: '1px solid #374151', paddingBottom: '16px' }}
        >
          {quickLinks.map((link) => {
            const isLoading = loadingItem === link.label;
            return (
              <UnstyledButton
                key={link.label}
                className="quick-link-item"
                style={isLoading ? { pointerEvents: 'none', opacity: 0.6 } : {}}
              >
                <Link
                  href={link.link}
                  className="menu-item flex items-center space-x-3 px-4 py-3 rounded-md text-gray-300 hover:bg-gray-700 hover:text-white transition duration-150 text-decoration-none"
                  onClick={() => {
                    setLoadingItem(link.label);
                    closeSidebar();
                  }}
                >
                  {isLoading ? <Loader size="24" color="orange" /> : <link.icon size={24} color="orange" />}
                  <div className="flex flex-col">
                    <Text size="sm" fw={500} className="text-white">
                      {link.label}
                    </Text>
                    <Text size="xs" className="text-gray-400">
                      {link.description}
                    </Text>
                  </div>
                </Link>
              </UnstyledButton>
            );
          })}
        </Box>

        {menuData.map((section) => {
          // Filter items based on login status and user roles
          const filteredItems = section.items.filter(
            (item) =>
              (!item.requiresLogin || user) && (!item.roles || item.roles.some((role) => userRoles.includes(role))),
          );

          return filteredItems.length > 0 ? (
            <Box key={section.title} className="section w-full">
              <UnstyledButton
                onClick={() => toggleSection(section.title)}
                className="section-header flex items-center space-x-2 justify-start"
              >
                {section.icon && <section.icon size={24} className="section-icon mr-3" />}
                <Text size="md" fw={600} className="section-title w-full text-left">
                  {section.title}
                </Text>
                {openSections[section.title] ? <IconChevronUp size={24} /> : <IconChevronDown size={24} />}
              </UnstyledButton>

              <Collapse in={openSections[section.title]}>
                <Box className="section-items">
                  {filteredItems.map((item) => {
                    // Check if this item is loading
                    const isLoading = loadingItem === item.label;
                    return (
                      <UnstyledButton
                        key={item.label}
                        className="menu-item text"
                        // Disable pointer events and reduce opacity if loading
                        style={isLoading ? { pointerEvents: 'none', opacity: 0.6 } : {}}
                      >
                        <Link
                          key={item.label}
                          href={getDynamicLink(item.link)}
                          className="menu-item flex items-center space-x-2 px-4 py-2 rounded-md text-gray-300 hover:bg-gray-700 hover:text-white transition duration-150 menu-item text-decoration-none"
                          onClick={() => {
                            setLoadingItem(item.label);
                            closeSidebar();
                          }}
                        >
                          {isLoading ? (
                            <Loader size="28" color="orange" mr="md" />
                          ) : (
                            <item.icon size={20} className="menu-icon" color="orange" />
                          )}
                          <Text size="sm" className="menu-label">
                            {item.label}
                          </Text>
                        </Link>
                      </UnstyledButton>
                    );
                  })}
                </Box>
              </Collapse>
            </Box>
          ) : null;
        })}

        <div className="border-t border-gray-700 pt-4">
          {user ? (
            <div>
              <input type="hidden" name="_method" value="delete" />
              <button
                type="button"
                onClick={handleLogout}
                className="flex items-center space-x-2 text-gray-300 hover:text-white px-4 py-3 rounded-md transition duration-150 menu-item mt-24 poppins"
              >
                <IconLogout size={20} />
                <span>Logout</span>
              </button>
            </div>
          ) : (
            <div className="flex flex-col space-y-2">
              <a
                href={getLoginUrl()}
                className="menu-item flex items-center space-x-2 px-4 py-2 rounded-md bg-gray-600 text-gray-300 hover:text-white transition duration-150 text-decoration-none poppins"
              >
                <IconLogin size={30} className="menu-icon" />
                <span>Log in</span>
              </a>
              <a
                href="/register"
                className="menu-item flex items-center space-x-2 px-4 py-2 rounded-md bg-gray-600 text-white transition duration-150 text-decoration-none poppins"
              >
                <IconUserPlus size={30} className="menu-icon" />
                <span>Sign up</span>
              </a>
            </div>
          )}
        </div>
      </div>
    </MantineProvider>
  );
};

export default Sidebar;
